/**
 * The model as it appears in a printed report.
 *
 * PRD §4.8 makes reproducibility a shipping requirement, and PRD §2 gives the
 * reviewer persona "full input listing in the report" as what they need. The
 * report had neither: the print stylesheet hides the whole model column, so a
 * printed analysis carried its results, its seed and its iteration count, but
 * not one word about the model that produced them. A reader could re-run it
 * only by asking the author for the project file.
 *
 * Screen-hidden and print-visible, so it duplicates nothing a user is already
 * looking at.
 */

import type { Project } from '../../engine/model.ts';
import { describeDistribution, describeRange, describeTolerancePercent } from '../summary.ts';

export function ModelSummary({ project }: { project: Project }) {
  const intermediates = project.intermediates ?? [];
  const groups = project.groups ?? [];
  const correlations = project.correlations ?? [];

  return (
    <section className="model-summary">
      <h2>Model</h2>

      <table className="data-table">
        <caption>Variables</caption>
        <thead>
          <tr>
            <th scope="col">Variable</th>
            <th scope="col">Description</th>
            <th scope="col">Distribution</th>
            <th scope="col">Value</th>
            <th scope="col">Tolerance</th>
            <th scope="col">Range</th>
            <th scope="col">Group</th>
          </tr>
        </thead>
        <tbody>
          {project.variables.map((v) => (
            <tr key={v.id}>
              <td>
                <code>{v.id}</code>
              </td>
              <td>{v.name ?? ''}</td>
              <td>{v.distribution.kind}</td>
              <td>{describeDistribution(v.distribution, v.unit)}</td>
              <td>{describeTolerancePercent(v.distribution, v.unit) ?? '—'}</td>
              <td>{describeRange(v.distribution, v.unit) ?? 'unbounded'}</td>
              <td>{v.group ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {(groups.length > 0 || correlations.length > 0) && (
        <table className="data-table">
          <caption>Correlation</caption>
          <thead>
            <tr>
              <th scope="col">Between</th>
              <th scope="col">ρ</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <tr key={g.id}>
                <td>
                  group <code>{g.id}</code>
                  {g.label ? ` — ${g.label}` : ''}
                </td>
                <td>{g.rho.toFixed(2)}</td>
              </tr>
            ))}
            {correlations.map((c, i) => (
              <tr key={`c${i}`}>
                <td>
                  <code>{c.a}</code> ↔ <code>{c.b}</code>
                </td>
                <td>{c.rho.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <table className="data-table">
        <caption>Transfer function</caption>
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Expression</th>
            <th scope="col">Unit</th>
            <th scope="col">LSL</th>
            <th scope="col">Target</th>
            <th scope="col">USL</th>
          </tr>
        </thead>
        <tbody>
          {intermediates.map((m) => (
            <tr key={m.id}>
              <td>
                <code>{m.id}</code>
              </td>
              <td>
                <code>{m.expr}</code>
              </td>
              <td>{m.unit ?? ''}</td>
              <td colSpan={3} className="muted">
                intermediate
              </td>
            </tr>
          ))}
          {project.outputs.map((o) => (
            <tr key={o.id}>
              <td>
                <strong>
                  <code>{o.id}</code>
                </strong>
              </td>
              <td>
                <code>{o.expr}</code>
              </td>
              <td>{o.unit ?? ''}</td>
              <td>{o.lsl ?? '—'}</td>
              <td>{o.target ?? '—'}</td>
              <td>{o.usl ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
